import { ChangeEvent, useCallback, useMemo, useState } from "react";

import { getEmployeeList } from "src/apis/employeeApi";
import { BRDiv } from "src/components/common/common";
import { CodeForm } from "src/components/common/form/CodeForm";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { InputForm } from "src/components/common/form/input/InputForm";
import { SingleDateForm } from "src/components/common/form/SingleDateForm";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";

import {
	defaultTQC2011_R01Info,
	useTQC2011_R01Store,
} from "./Store_TQC2011_R01";

interface Props {
	onInspectTypeChange: (inspectType: string) => void;
}

export const Info_TQC2011_R01 = ({ onInspectTypeChange }: Props) => {
	const {
		TQC2011_R01Info,
		setTQC2011_R01Info,
		selectedTQC2011_R01,
	} = useTQC2011_R01Store();
	const { inspectionTypeList } = useCommonCodeStore();
	const { setCodeSearchModalProps } = useUiuxStore();
	const [inspectDtVisible, setInspectDtVisible] = useState(false);
	const [inspectDtPickerPos, setInspectDtPickerPos] = useState({
		top: 0,
		left: 0,
		height: 0,
	});
	const fmlInspectionTypes = useMemo(
		() => inspectionTypeList.filter((item) => item.ref01 === "FML"),
		[inspectionTypeList],
	);
	const noopChange = useCallback(() => undefined, []);

	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: "inspectorEmp",
			category: "검사자",
			focusId: "tqc2011_inspectorEmp",
			responseMapping: { empNo: "inspectorEmp", empNm: "inspectorEmpNm" },
			searchFunction: async (code: string) => {
				const response = await getEmployeeList("all");
				return response.data.data.find((value) => value.empNo === code);
			},
		},
		{
			fieldName: "approvorEmpno",
			category: "승인자",
			focusId: "tqc2011_approvorEmpno",
			responseMapping: { empNo: "approvorEmpno", empNm: "approvorEmpNm" },
			searchFunction: async (code: string) => {
				const response = await getEmployeeList("all");
				return response.data.data.find((value) => value.empNo === code);
			},
		},
	];

	const { getFieldHandlers } = useInfoFormEvents({
		editData: TQC2011_R01Info,
		setEditData: setTQC2011_R01Info,
		defaultData: defaultTQC2011_R01Info,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: "TQC2011_R01",
	});

	const handleInspectTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
		onInspectTypeChange(event.target.value);
	};

	return (
		<div className="w-full h-full flex flex-wrap content-start pt-[12px] pl-[12px] pr-[12px] pb-[6px] overflow-y-auto border border-gray-300 bg-white">
			<InputForm
				title="사업장"
				name="compNm"
				value={selectedTQC2011_R01?.compNm ?? ""}
				editable={false}
			/>
			<InputForm
				title="작업장"
				name="workplaceNm"
				value={selectedTQC2011_R01?.workplaceNm ?? ""}
				editable={false}
			/>
			<CodeForm
				label="작업품목"
				name="itemCd"
				value={selectedTQC2011_R01?.itemCd ?? ""}
				custNmValue={selectedTQC2011_R01?.itemNm ?? ""}
				editable={false}
				handleChange={noopChange}
			/>
			<CodeForm
				label="작업자"
				name="productEmp"
				value={selectedTQC2011_R01?.productEmp ?? ""}
				custNmValue={selectedTQC2011_R01?.productEmpNm ?? ""}
				editable={false}
				handleChange={noopChange}
			/>
			<BRDiv />
			<DropDownForm
				title="검사구분"
				name="inspectType"
				value={TQC2011_R01Info.inspectType}
				data={fmlInspectionTypes}
				initLabelLess
				editable={Boolean(selectedTQC2011_R01)}
				onChange={handleInspectTypeChange}
			/>
			<CodeForm
				label="검사자"
				name="inspectorEmp"
				value={TQC2011_R01Info.inspectorEmp}
				custNmValue={TQC2011_R01Info.inspectorEmpNm}
				editable={Boolean(selectedTQC2011_R01)}
				handleFoucsId="tqc2011_inspectorEmp"
				handleChange={getFieldHandlers("inspectorEmp").onChange}
				onDoubleClick={getFieldHandlers("inspectorEmp").onDoubleClick}
				onKeyDown={getFieldHandlers("inspectorEmp").onEnterKeyDown}
			/>
			<SingleDateForm
				title="검사일자"
				value={TQC2011_R01Info.inspectDt}
				setValue={(inspectDt) => setTQC2011_R01Info({ inspectDt })}
				editable={Boolean(selectedTQC2011_R01)}
				visible={inspectDtVisible}
				setVisible={setInspectDtVisible}
				pickerPos={inspectDtPickerPos}
				setPickerPos={setInspectDtPickerPos}
				placeholder="검사일자 선택"
			/>
			<CodeForm
				label="승인자"
				name="approvorEmpno"
				value={TQC2011_R01Info.approvorEmpno}
				custNmValue={TQC2011_R01Info.approvorEmpNm}
				editable={Boolean(selectedTQC2011_R01)}
				handleFoucsId="tqc2011_approvorEmpno"
				handleChange={getFieldHandlers("approvorEmpno").onChange}
				onDoubleClick={getFieldHandlers("approvorEmpno").onDoubleClick}
				onKeyDown={getFieldHandlers("approvorEmpno").onEnterKeyDown}
			/>
			<InputForm
				title="비고"
				name="inspectRemark"
				value={TQC2011_R01Info.inspectRemark}
				editable={Boolean(selectedTQC2011_R01)}
				onChange={(event) =>
					setTQC2011_R01Info({ inspectRemark: event.target.value })
				}
			/>
		</div>
	);
};
